import _extends from "@babel/runtime/helpers/esm/extends";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames/bind';
import { bool, func, instanceOf, number, oneOf, oneOfType, string } from 'prop-types';
import { disabledDaysPropType } from "../../../Datepicker";
import { ChevronNormalLeft, ChevronNormalRight } from "../../../icons";
import { TextBase } from "../../../internal/TextBase";
import { getDisabledTimestamps, getLastDayOfMonth } from "../../../utils/date";
import { MONTHS } from "../../../utils/date/constants";
import { getMethodsForTestId } from "../../../utils/getMethodsForTestId";
import { isDate } from "../../../utils/type";
import styles from './MonthPicker.css';
var cx = classNames.bind(styles);

var setMonth = function setMonth(_ref) {
  var active = _ref.active,
      byNumber = _ref.byNumber,
      event = _ref.event,
      onChange = _ref.onChange,
      selectedYear = _ref.selectedYear,
      setYearValue = _ref.setYearValue;
  var _event$currentTarget$ = event.currentTarget.dataset,
      month = _event$currentTarget$.month,
      year = _event$currentTarget$.year;
  month = parseInt(month, 10);
  year = parseInt(year, 10);

  if (month === active && year === selectedYear) {
    return;
  }

  if (!byNumber) {
    month = new Date(year, month, 2);
  }

  setYearValue(year);
  onChange(month, year);
};

var getActive = function getActive(_ref2) {
  var selectedYear = _ref2.selectedYear,
      value = _ref2.value,
      yearValue = _ref2.yearValue,
      withYear = _ref2.withYear;
  var month = isDate(value) ? value.getMonth() : value;

  if (withYear && selectedYear === yearValue) {
    return month;
  }

  if (!withYear && (month < new Date().getMonth() ? selectedYear === yearValue - 1 : selectedYear === yearValue)) {
    return month;
  }

  return null;
};

var getSelectedYear = function getSelectedYear(year) {
  return year ? parseInt(year, 10) : new Date().getFullYear();
};

var getYearValue = function getYearValue(value, year) {
  if (isDate(value)) {
    return value.getFullYear();
  }

  return year ? parseInt(year, 10) : new Date().getFullYear();
};

var isDisabled = function isDisabled(start, end) {
  var disabledDaysTimestampMap = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  var max = disabledDaysTimestampMap.max,
      min = disabledDaysTimestampMap.min,
      ranges = disabledDaysTimestampMap.ranges;

  if (max && start > max || min && end < min || ranges && ranges.some(function (_ref3) {
    var from = _ref3.from,
        to = _ref3.to;
    return start >= from && end <= to;
  })) {
    return true;
  }

  return false;
};

var isMonthDisabled = function isMonthDisabled(month, year, disabledDaysTimestampMap) {
  var lastDayOfMonth = getLastDayOfMonth(month + 1, year);
  var start = new Date(year, month, 1, 0, 0, 0, 0).getTime();
  var end = new Date(year, month, lastDayOfMonth, 23, 59, 59, 999).getTime();
  return isDisabled(start, end, disabledDaysTimestampMap);
};

var isYearDisabled = function isYearDisabled(year, disabledDaysTimestampMap) {
  var start = new Date(year, 0, 1, 0, 0, 0, 0).getTime();
  var end = new Date(year, 11, 31, 23, 59, 59, 999).getTime();
  return isDisabled(start, end, disabledDaysTimestampMap);
};

var isLeftDisabled = function isLeftDisabled(year, disabledDaysTimestampMap) {
  return isYearDisabled(year - 1, disabledDaysTimestampMap);
};

var isRightDisabled = function isRightDisabled(year, disabledDaysTimestampMap) {
  return isYearDisabled(year + 1, disabledDaysTimestampMap);
};

var getGroupedMonthsFromCurrent = function getGroupedMonthsFromCurrent(locale, year, disabledDaysTimestampMap) {
  var months = MONTHS[locale];
  var start = new Date().getMonth();
  var groups = [[], [], [], []];
  var yearOfMonth = year;
  var i = start;
  var group = 0;

  do {
    if (groups[group].length === 3) {
      group += 1;
    }

    groups[group].push([months[i], i, yearOfMonth, isMonthDisabled(i, yearOfMonth, disabledDaysTimestampMap)]);

    if (i === 11) {
      yearOfMonth += 1;
      i = 0;
    } else {
      i += 1;
    }
  } while (i !== start);

  return groups;
};

var getGroupedMonths = function getGroupedMonths(locale, year, disabledDaysTimestampMap) {
  var months = MONTHS[locale];
  var groups = [];
  months.forEach(function (month, i) {
    var group = Math.floor(i / 3);
    var monthItem = [month, i, year, isMonthDisabled(i, year, disabledDaysTimestampMap)];

    if (groups[group]) {
      groups[group].push(monthItem);
    } else {
      groups[group] = [monthItem];
    }
  });
  return groups;
};

var MonthPicker = function MonthPicker(_ref4) {
  var byNumber = _ref4.byNumber,
      cellPadding = _ref4.cellPadding,
      className = _ref4.className,
      disabledDays = _ref4.disabledDays,
      locale = _ref4.locale,
      namespace = _ref4.namespace,
      onChange = _ref4.onChange,
      onChangeYear = _ref4.onChangeYear,
      theme = _ref4.theme,
      value = _ref4.value,
      withYear = _ref4.withYear,
      year = _ref4.year,
      yearValueProp = _ref4.yearValue;

  var _getMethodsForTestId = getMethodsForTestId(namespace, 'monthPicker'),
      getTestId = _getMethodsForTestId.getTestId;

  var _useState = useState(yearValueProp),
      _useState2 = _slicedToArray(_useState, 2),
      yearValue = _useState2[0],
      setYearValue = _useState2[1];

  var _useState3 = useState(year),
      _useState4 = _slicedToArray(_useState3, 2),
      selectedYear = _useState4[0],
      setSelectedYear = _useState4[1];

  var disabledDaysTimestampMap = useMemo(function () {
    return getDisabledTimestamps(disabledDays);
  }, [disabledDays]);
  var leftDisabled = useMemo(function () {
    return isLeftDisabled(selectedYear, disabledDaysTimestampMap);
  }, [disabledDaysTimestampMap, selectedYear]);
  var rightDisabled = useMemo(function () {
    return isRightDisabled(selectedYear, disabledDaysTimestampMap);
  }, [disabledDaysTimestampMap, selectedYear]);
  var active = useMemo(function () {
    return getActive({
      selectedYear: selectedYear,
      value: value,
      withYear: withYear,
      yearValue: yearValue
    });
  }, [selectedYear, value, withYear, yearValue]);
  var groups = useMemo(function () {
    return (withYear ? getGroupedMonths : getGroupedMonthsFromCurrent)(locale, selectedYear, disabledDaysTimestampMap);
  }, [disabledDaysTimestampMap, locale, selectedYear, withYear]);
  useEffect(function () {
    setSelectedYear(getSelectedYear(year));
  }, [year]);
  useEffect(function () {
    setYearValue(getYearValue(value, yearValue));
  }, [value, yearValue]);
  var handleChange = useCallback(function (event) {
    setMonth({
      active: active,
      byNumber: byNumber,
      event: event,
      onChange: onChange,
      selectedYear: selectedYear,
      setYearValue: setYearValue
    });
  }, [active, byNumber, onChange, selectedYear]);
  var prevYear = useCallback(function () {
    var newSelectedYear = selectedYear - 1;
    setSelectedYear(newSelectedYear);

    if (onChangeYear) {
      onChangeYear(newSelectedYear);
    }
  }, [onChangeYear, selectedYear]);
  var nextYear = useCallback(function () {
    var newSelectedYear = selectedYear + 1;
    setSelectedYear(newSelectedYear);

    if (onChangeYear) {
      onChangeYear(newSelectedYear);
    }
  }, [onChangeYear, selectedYear]);

  var renderMonth = function renderMonth(_ref5) {
    var _ref6 = _slicedToArray(_ref5, 4),
        month = _ref6[0],
        monthNumber = _ref6[1],
        yearOfMonth = _ref6[2],
        disabled = _ref6[3];

    return /*#__PURE__*/React.createElement("td", {
      key: month
    }, /*#__PURE__*/React.createElement("button", _extends({
      className: cx('Month', {
        Month_active: active === monthNumber,
        Month_disabled: disabled
      }),
      "data-month": monthNumber,
      "data-year": yearOfMonth,
      onClick: disabled ? undefined : handleChange,
      type: "button"
    }, getTestId('month', monthNumber)), /*#__PURE__*/React.createElement(TextBase, {
      alignment: "center",
      color: disabled ? 'N100' : 'N900',
      size: "s",
      weight: "semibold"
    }, month), /*#__PURE__*/React.createElement(TextBase, {
      alignment: "center",
      color: "N100",
      size: "s"
    }, yearOfMonth)));
  };

  var renderGroup = function renderGroup(months, i) {
    return /*#__PURE__*/React.createElement("tr", {
      key: i
    }, months.map(renderMonth));
  };

  return /*#__PURE__*/React.createElement("div", _extends({
    className: cx('Wrapper')
  }, getTestId()), withYear && /*#__PURE__*/React.createElement("div", _extends({
    className: cx('YearSelection')
  }, getTestId('year')), /*#__PURE__*/React.createElement("button", _extends({
    className: cx('Chevron', {
      Chevron_disabled: leftDisabled
    }),
    onClick: leftDisabled ? undefined : prevYear,
    type: "button"
  }, getTestId('year', 'left')), /*#__PURE__*/React.createElement(ChevronNormalLeft, null)), selectedYear, /*#__PURE__*/React.createElement("button", _extends({
    className: cx('Chevron', {
      Chevron_disabled: rightDisabled
    }),
    onClick: rightDisabled ? undefined : nextYear,
    type: "button"
  }, getTestId('year', 'right')), /*#__PURE__*/React.createElement(ChevronNormalRight, null))), /*#__PURE__*/React.createElement("table", {
    className: cx('MonthPicker', "MonthPicker_theme_".concat(theme), className),
    cellPadding: cellPadding,
    cols: 3
  }, /*#__PURE__*/React.createElement("tbody", null, groups.map(renderGroup))));
};

export var locales = Object.keys(MONTHS);
export var themes = ['b2c', 'b2b'];
MonthPicker.propTypes = process.env.NODE_ENV !== "production" ? {
  // Используется для включения режима при котором выбранный месяц будет
  // выставляться посредством числа, обозначающего номер месяца от 0 до 11,
  // то есть функция onChange будет передавать первым аргументом номер
  // выбранного месяца. Если byNumber равен false, то onChange будет передавать
  // объект даты.
  byNumber: bool,
  cellPadding: number,
  className: string,
  disabledDays: disabledDaysPropType,
  locale: oneOf(locales),
  namespace: string,
  onChange: func,
  onChangeYear: func,
  theme: oneOf(themes),
  value: oneOfType([instanceOf(Date), number]),
  // eslint-disable-next-line react/boolean-prop-naming
  withYear: bool,
  year: oneOfType([number, string]),
  // Используется для контроля года у выбранного месяца если выставлен параметр
  // byNumber, чтобы при выставленном параметре withYear можно было определить
  // в каком именно году подсвечивать выбранный месяц.
  yearValue: oneOfType([number, string])
} : {};
MonthPicker.defaultProps = {
  byNumber: false,
  cellPadding: 2,
  locale: 'ru',
  theme: themes[0],
  withYear: false
};
export default MonthPicker;